import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ListChecks, Lock, PartyPopper, Pencil, Plus, StickyNote, Trash2, Zap } from 'lucide-react';
import { apiPersons, apiLogtime, apiLoggedTime, apiSchedule } from '../api/client.js';
import DurationInput, { formatHHMM } from '../components/DurationInput.jsx';
import MobileMonthGrid from './MobileMonthGrid.jsx';
import MobileLogtimeAddModal from './MobileLogtimeAddModal.jsx';
import MobileLogtimeEditModal from './MobileLogtimeEditModal.jsx';
import { useMonthCursor } from './mobileView.js';
import {
  todayUtc,
  mondayOf,
  addDays,
  isoDate,
  parseIso,
  fullDayLabel,
  weekRangeLabel,
  monthGridDays,
  safeColor,
} from './dateUtils.js';

/**
 * Logtime mobile — lecture + saisie. Une ligne par couple (affaire × jour)
 * issue du planning / des saisies / des suggestions. Réutilise le CRUD
 * `/api/logged-time` (IDOR + verrou comptable côté backend).
 *
 * Deux modes (`viewMode`) :
 *  - `list` : vue SEMAINE (un `GET /api/logtime/week`) ;
 *  - `calendar` : grille MENSUELLE, un `GET /api/logtime/week` par semaine
 *    affichée (4 à 6 en parallèle) — `/api/logtime/calendar` est hors de la
 *    liste blanche de l'app native (`api/src/lib/mobileScope.ts`). Les jours
 *    fériés viennent de `GET /api/schedule?withHolidays=1` (liste blanche),
 *    comme le planning. Le jour sélectionné est rendu par le même `DayCard`
 *    que la liste.
 */

// Une date est verrouillée si lock && date <= lockDate ET non couverte par un
// grant actif (fromDate <= date <= toDate).
function isDateLockedIn(week, iso) {
  const lock = week?.lock;
  if (!lock?.lockDate) return false;
  if (iso > lock.lockDate) return false;
  const grants = week?.unlockGrants ?? [];
  const covered = grants.some(
    (g) => String(g.fromDate).slice(0, 10) <= iso && iso <= String(g.toDate).slice(0, 10),
  );
  return !covered;
}

// Heures contractuelles d'un jour (index getUTCDay) — même règle que
// `baseHoursForDay` côté serveur (api/src/lib/capacity.ts) : `workDaysHours`
// si renseigné, sinon 8 h du lundi au vendredi.
function baseHoursForDay(wdh, dow) {
  const DEFAULT = dow >= 1 && dow <= 5 ? 8 : 0;
  if (!wdh || typeof wdh !== 'object') return DEFAULT;
  const keys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const values = Array.isArray(wdh) ? wdh : keys.map((k) => wdh[k]);
  if (values.length !== 7 || !values.some((v) => Number(v) > 0)) return DEFAULT;
  const n = Number(values[dow]);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Statut d'un jour pour la grille mensuelle :
 *  - `logged`   : heures saisies ;
 *  - `expected` : CAPACITÉ du jour, même définition que le tableau de bord et
 *    les rapports (`dayCapacity` serveur) : heures contractuelles, 0 un jour
 *    férié, moins les congés APPROUVÉS (journée entière ⇒ 0, sinon leurs
 *    heures — le temps partiel est un congé récurrent). Ce n'est PAS le
 *    planning : 2 h saisies sur une journée de 8 h restent « partiel » même
 *    si seules 2 h étaient planifiées ;
 *  - `suggested`: heures encore suggérées (planifiées non saisies) ;
 *  - `status`   : `complete` | `partial` | `missing` sur jour passé ou
 *    aujourd'hui avec une capacité, `null` sinon (futur, week-end, férié…).
 */
function dayStatus(week, iso, { holiday, todayIso }) {
  const logged = (week?.totalsByDay ?? []).find((t) => t.date === iso)?.totalHours ?? 0;
  const suggested = (week?.suggestions ?? [])
    .filter((sg) => sg.date === iso)
    .reduce((acc, sg) => acc + (Number(sg.hoursPerDay) || 0), 0);
  const date = parseIso(iso);
  const base = holiday ? 0 : baseHoursForDay(week?.person?.workDaysHours, date.getUTCDay());
  const offs = ((week?.timeOffsByDay ?? []).find((t) => t.date === iso)?.timeOffs ?? []).filter(
    (t) => t.status === 'APPROVED',
  );
  const offHours = offs.reduce(
    (acc, t) => acc + (t.fullDay !== false ? base : Number(t.hoursPerDay) || 0),
    0,
  );
  const expected = Math.max(0, base - offHours);
  const working = expected > 0;
  let status = null;
  if (working && iso <= todayIso && (expected > 0 || logged > 0)) {
    if (logged + 1e-6 >= expected) status = 'complete';
    else if (logged > 0) status = 'partial';
    else status = 'missing';
  } else if (working && logged + 1e-6 >= expected) {
    // Jour futur déjà entièrement saisi (saisie anticipée) : complet. Un jour
    // futur incomplet reste neutre — ce n'est pas encore un retard.
    status = 'complete';
  }
  return { logged, expected, suggested, status };
}

const STATUS_LABEL = {
  complete: 'complet',
  partial: 'partiel',
  missing: 'non saisi',
};

export default function MobileLogtime({ viewMode = 'list' }) {
  const isCalendar = viewMode === 'calendar';
  const [weekStart, setWeekStart] = useState(() => mondayOf(todayUtc()));
  const { month, setMonth, selectedIso, setSelectedIso } = useMonthCursor();
  const [myPersonId, setMyPersonId] = useState(null);
  const [personResolved, setPersonResolved] = useState(false);
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Calendrier : { [lundi ISO]: réponse /logtime/week } + fériés du mois.
  const [calWeeks, setCalWeeks] = useState({});
  const [calHolidays, setCalHolidays] = useState([]);
  const [calLoading, setCalLoading] = useState(true);
  const [calError, setCalError] = useState(null);
  // Jeton de génération : toute réponse d'une génération antérieure (mois
  // quitté, mode changé) est ignorée. `/logtime/week` n'accepte pas de
  // `signal` côté client : on ignore plutôt qu'on annule.
  const calGenRef = useRef(0);
  const calAbortRef = useRef(null);
  const [toast, setToast] = useState(null); // { kind, message }
  // Jour (ISO) pour lequel la modale « Ajouter une affaire » est ouverte.
  const [addDate, setAddDate] = useState(null);
  // Saisie existante ouverte en détail : { entry, locked, startDelete }.
  const [editing, setEditing] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  // Anti-double-tap (F-MOB-09) : verrou synchrone par rowKey pour ignorer un
  // second commit pendant qu'une mutation est déjà en vol sur la même ligne
  // (le state `savingKey` serait stale dans le callback).
  const inFlightRef = useRef(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await apiPersons.me();
        if (!cancelled) setMyPersonId(me?.id ?? null);
      } catch {
        if (!cancelled) setMyPersonId(null);
      } finally {
        if (!cancelled) setPersonResolved(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchWeek = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiLogtime.week({
        weekStart: isoDate(weekStart),
        personId: myPersonId ?? undefined,
      });
      setWeek(data);
      setError(null);
    } catch {
      setError('load');
    } finally {
      setLoading(false);
    }
  }, [weekStart, myPersonId]);

  useEffect(() => {
    if (!personResolved || isCalendar) return;
    fetchWeek();
  }, [personResolved, isCalendar, fetchWeek]);

  // ---- Calendrier : une requête /logtime/week par semaine de la grille ----
  const gridDays = useMemo(() => monthGridDays(month), [month]);
  const gridWeekStarts = useMemo(() => {
    const out = [];
    for (let i = 0; i < gridDays.length; i += 7) out.push(isoDate(gridDays[i]));
    return out;
  }, [gridDays]);
  const gridFrom = isoDate(gridDays[0]);
  const gridTo = isoDate(gridDays[gridDays.length - 1]);

  const fetchMonth = useCallback(async () => {
    const gen = ++calGenRef.current;
    calAbortRef.current?.abort();
    const ctrl = new AbortController();
    calAbortRef.current = ctrl;
    setCalLoading(true);
    try {
      const [weeks, sched] = await Promise.all([
        Promise.all(
          gridWeekStarts.map((ws) =>
            apiLogtime.week({ weekStart: ws, personId: myPersonId ?? undefined }),
          ),
        ),
        // Fériés seulement (best effort : un échec n'empêche pas la saisie).
        myPersonId
          ? apiSchedule
              .get({
                from: gridFrom,
                to: gridTo,
                personIds: [myPersonId],
                withHolidays: true,
                signal: ctrl.signal,
              })
              .catch(() => null)
          : Promise.resolve(null),
      ]);
      if (gen !== calGenRef.current) return; // réponse obsolète
      setCalWeeks(Object.fromEntries(gridWeekStarts.map((ws, i) => [ws, weeks[i]])));
      setCalHolidays((myPersonId && sched?.holidaysByPerson?.[myPersonId]) || []);
      setCalError(null);
    } catch {
      if (gen !== calGenRef.current) return;
      setCalError('load');
    } finally {
      if (gen === calGenRef.current) setCalLoading(false);
    }
  }, [gridWeekStarts, gridFrom, gridTo, myPersonId]);

  useEffect(() => {
    if (!personResolved || !isCalendar) return undefined;
    fetchMonth();
    return () => {
      // Démontage / changement de mois ou de mode : invalide la génération.
      calGenRef.current += 1;
      calAbortRef.current?.abort();
    };
  }, [personResolved, isCalendar, fetchMonth]);

  // Recharge la seule semaine touchée après une saisie (calendrier).
  const refreshCalWeek = useCallback(
    async (iso) => {
      const ws = isoDate(mondayOf(parseIso(iso)));
      const gen = calGenRef.current;
      try {
        const data = await apiLogtime.week({ weekStart: ws, personId: myPersonId ?? undefined });
        if (gen !== calGenRef.current) return;
        setCalWeeks((prev) => ({ ...prev, [ws]: data }));
      } catch {
        /* la grille garde l'état précédent ; la saisie a déjà été notifiée */
      }
    },
    [myPersonId],
  );

  // Rafraîchissement après mutation, selon le mode.
  const refresh = useCallback(
    (iso) => (isCalendar ? refreshCalWeek(iso) : fetchWeek()),
    [isCalendar, refreshCalWeek, fetchWeek],
  );

  const weekOf = useCallback(
    (iso) => calWeeks[isoDate(mondayOf(parseIso(iso)))] ?? null,
    [calWeeks],
  );
  const selectedWeek = isCalendar ? weekOf(selectedIso) : null;

  const showToast = useCallback((kind, message) => {
    setToast({ kind, message });
    setTimeout(() => setToast(null), 3000);
  }, []);

  function handleApiError(e) {
    // 423 = verrou comptable côté backend.
    if (e?.status === 423) showToast('error', 'Période verrouillée.');
    // 403 = refus métier (ex. affaire en planification uniquement) : message
    // serveur ; en app native, un 403 `mobile_scope` porte déjà un message
    // lisible (cf. client.js).
    else if (e?.status === 403 && e?.message) showToast('error', e.message);
    else showToast('error', 'Échec de l’enregistrement.');
  }

  // Résout l'effective personId (réponse ou me()).
  const refWeek = isCalendar ? selectedWeek : week;
  const personId = myPersonId ?? refWeek?.myPersonId ?? refWeek?.person?.id ?? null;

  // Semaine portant une date donnée (liste : la semaine chargée).
  const weekFor = useCallback(
    (iso) => (isCalendar ? weekOf(iso) : week),
    [isCalendar, weekOf, week],
  );

  // Enregistre les heures pour une (affaire, date). `existing` = loggedTime
  // existant (peut être null). Source planning si rattaché à une allocation.
  const saveHours = useCallback(
    async (rowKey, { existing, affaireId, phaseId, allocationId, date, billable }, hours) => {
      if (personId == null) return;
      if (inFlightRef.current.has(rowKey)) return; // double-tap ignoré
      inFlightRef.current.add(rowKey);
      setSavingKey(rowKey);
      try {
        if (existing) {
          if (hours <= 0) await apiLoggedTime.archive(existing.id);
          else await apiLoggedTime.update(existing.id, { hours });
        } else if (hours > 0) {
          await apiLoggedTime.create({
            personId,
            affaireId,
            phaseId: phaseId ?? null,
            allocationId: allocationId ?? null,
            date,
            hours,
            billable: Boolean(billable),
            source: allocationId ? 'planning' : 'manual',
          });
        }
        await refresh(date);
      } catch (e) {
        handleApiError(e);
      } finally {
        inFlightRef.current.delete(rowKey);
        setSavingKey(null);
      }
    },
    [personId, refresh],
  );

  // Log all : crée toutes les suggestions non lockées du jour.
  const logAllDay = useCallback(
    async (dayIso) => {
      const w = weekFor(dayIso);
      const suggestions = (w?.suggestions ?? []).filter(
        (s) => s.date === dayIso && !s.isLocked && !isDateLockedIn(w, dayIso),
      );
      if (suggestions.length === 0) return;
      try {
        const entries = suggestions.map((s) => ({
          personId,
          affaireId: s.affaireId,
          phaseId: s.phaseId ?? null,
          allocationId: s.allocationId ?? null,
          date: s.date,
          hours: s.hoursPerDay,
          billable: Boolean(s.billable),
          source: 'planning',
        }));
        const res = await apiLoggedTime.bulkCreate(entries);
        showToast(
          'ok',
          `${res.created} saisie(s) créée(s)${res.skipped ? `, ${res.skipped} ignorée(s)` : ''}.`,
        );
        await refresh(dayIso);
      } catch (e) {
        handleApiError(e);
      }
    },
    [weekFor, personId, refresh, showToast],
  );

  const addModal = (
    <MobileLogtimeAddModal
      open={Boolean(addDate)}
      date={addDate}
      personId={personId}
      onClose={() => setAddDate(null)}
      onSaved={async (iso) => {
        setAddDate(null);
        showToast('ok', 'Imputation ajoutée.');
        await refresh(iso);
      }}
    />
  );

  const editModal = (
    <MobileLogtimeEditModal
      entry={editing?.entry ?? null}
      locked={Boolean(editing?.locked)}
      startDelete={Boolean(editing?.startDelete)}
      onClose={() => setEditing(null)}
      onSaved={async (iso, message) => {
        setEditing(null);
        showToast('ok', message);
        await refresh(iso);
      }}
    />
  );
  // Ouvre le détail d'une saisie (crayon) ou sa suppression (corbeille).
  const onEdit = (entry, locked, startDelete = false) =>
    setEditing({ entry, locked, startDelete });

  const toastEl = toast ? (
    <div className="m-toast" data-kind={toast.kind === 'error' ? 'error' : 'ok'}>
      {toast.message}
    </div>
  ) : null;

  if (isCalendar) {
    const todayIso = isoDate(todayUtc());
    const holidayByIso = new Map(calHolidays.map((h) => [String(h.date).slice(0, 10), h]));
    const statusOf = (iso) => {
      const w = weekOf(iso);
      return w ? dayStatus(w, iso, { holiday: holidayByIso.get(iso), todayIso }) : null;
    };
    const monthIdx = month.getUTCMonth();
    const monthTotal = gridDays
      .filter((d) => d.getUTCMonth() === monthIdx)
      .reduce((acc, d) => acc + (statusOf(isoDate(d))?.logged || 0), 0);

    const renderCell = (_d, iso) => {
      const st = statusOf(iso);
      const locked = isDateLockedIn(weekOf(iso), iso);
      return (
        <>
          {locked ? <Lock size={10} className="m-cal__lock" aria-hidden="true" /> : null}
          <span className="m-cal__foot">
            {st?.logged > 0 ? <span className="m-cal__hours">{formatHHMM(st.logged)}</span> : null}
          </span>
          <span className="m-cal__status" data-status={st?.status ?? 'none'} />
        </>
      );
    };
    const cellLabel = (_d, iso) => {
      const st = statusOf(iso);
      if (!st) return '';
      const parts = [`saisi ${formatHHMM(st.logged)}`];
      if (st.expected > 0) parts.push(`attendu ${formatHHMM(st.expected)}`);
      if (st.suggested > 0) parts.push(`suggéré ${formatHHMM(st.suggested)}`);
      if (st.status) parts.push(STATUS_LABEL[st.status]);
      if (isDateLockedIn(weekOf(iso), iso)) parts.push('verrouillé');
      return parts.join(', ');
    };

    const legend = (
      <div className="m-cal__legend" aria-hidden="true">
        <span><i data-status="complete" /> Complet</span>
        <span><i data-status="partial" /> Partiel</span>
        <span><i data-status="missing" /> Non saisi</span>
        <span><Lock size={11} /> Verrouillé</span>
      </div>
    );

    return (
      <div>
        <MobileMonthGrid
          month={month}
          onMonthChange={setMonth}
          selectedIso={selectedIso}
          onSelect={setSelectedIso}
          renderCell={renderCell}
          cellLabel={cellLabel}
          holidayByIso={holidayByIso}
          loading={calLoading}
          subtitle={calLoading ? null : `Total du mois : ${formatHHMM(monthTotal)}`}
          legend={legend}
        />
        <div className="m-cal__detail">
          {calLoading ? (
            <div className="m-state">Chargement…</div>
          ) : calError ? (
            <div className="m-state">Impossible de charger la saisie.</div>
          ) : personId == null ? (
            <div className="m-state">Aucune fiche collaborateur liée à votre compte.</div>
          ) : selectedWeek ? (
            <DayCard
              day={{ date: selectedIso }}
              date={parseIso(selectedIso)}
              week={selectedWeek}
              locked={isDateLockedIn(selectedWeek, selectedIso)}
              holiday={holidayByIso.get(selectedIso)}
              savingKey={savingKey}
              onSave={saveHours}
              onLogAll={logAllDay}
              onAdd={setAddDate}
              onEdit={onEdit}
            />
          ) : null}
        </div>
        {addModal}
        {editModal}
        {toastEl}
      </div>
    );
  }

  const days = week?.days ?? [];
  const weekTotal = (week?.totalsByDay ?? []).reduce((acc, d) => acc + (d.totalHours || 0), 0);

  return (
    <div>
      <div className="m-weeknav">
        <button
          type="button"
          className="m-navbtn"
          aria-label="Semaine précédente"
          onClick={() => setWeekStart((w) => addDays(w, -7))}
        >
          <ChevronLeft size={18} />
        </button>
        <span className="m-weeknav__title">
          {weekRangeLabel(weekStart)}
          <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}>
            Total : {formatHHMM(weekTotal)}
          </span>
        </span>
        <button
          type="button"
          className="m-navbtn"
          aria-label="Semaine suivante"
          onClick={() => setWeekStart((w) => addDays(w, 7))}
        >
          <ChevronRight size={18} />
        </button>
      </div>
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <button
          type="button"
          className="m-todaybtn"
          onClick={() => setWeekStart(mondayOf(todayUtc()))}
        >
          Cette semaine
        </button>
      </div>

      {loading ? (
        <div className="m-state">Chargement…</div>
      ) : error ? (
        <div className="m-state">Impossible de charger la saisie.</div>
      ) : personId == null ? (
        <div className="m-state">Aucune fiche collaborateur liée à votre compte.</div>
      ) : (
        days.map((d, idx) => (
          <DayCard
            key={d.date}
            day={d}
            date={addDays(weekStart, idx)}
            week={week}
            locked={isDateLockedIn(week, d.date)}
            holiday={d.holiday ? { name: d.holiday } : undefined}
            savingKey={savingKey}
            onSave={saveHours}
            onLogAll={logAllDay}
            onAdd={setAddDate}
            onEdit={onEdit}
          />
        ))
      )}

      {addModal}
      {editModal}
      {toastEl}
    </div>
  );
}

/**
 * Rendu d'UN jour de saisie (congés, lignes affaire × heures, « Tout
 * valider »), partagé par la liste semaine et le détail du jour sélectionné
 * dans la grille mensuelle. `holiday` : optionnel (calendrier uniquement).
 */
function DayCard({ day, date, week, locked, holiday, savingKey, onSave, onLogAll, onAdd, onEdit }) {
  const iso = day.date;
  const total = (week.totalsByDay ?? []).find((t) => t.date === iso)?.totalHours ?? 0;
  const logged = (week.loggedTimesByDay ?? []).find((l) => l.date === iso)?.items ?? [];
  const allocs = (week.allocationsByDay ?? []).find((a) => a.date === iso)?.allocations ?? [];
  const suggestions = (week.suggestions ?? []).filter((s) => s.date === iso);
  const timeOffs = (week.timeOffsByDay ?? []).find((t) => t.date === iso)?.timeOffs ?? [];

  // Construit la liste des lignes affichables : 1 par couple (affaire/phase).
  // On part des loggedTimes existants, puis on ajoute les allocations/suggestions
  // non encore loggées.
  const rows = [];
  const seen = new Set();
  const keyOf = (affaireId, phaseId) => `${affaireId}::${phaseId ?? ''}`;

  for (const lt of logged) {
    const k = keyOf(lt.affaireId, lt.phaseId);
    seen.add(k);
    rows.push({
      key: `lt-${lt.id}`,
      affaire: lt.affaire,
      phase: lt.phase,
      affaireId: lt.affaireId,
      phaseId: lt.phaseId,
      allocationId: lt.allocationId,
      billable: lt.billable,
      existing: lt,
      plannedHours: null,
      isSuggestion: false,
    });
  }
  for (const a of allocs) {
    const k = keyOf(a.affaireId, a.phaseId);
    if (seen.has(k)) continue;
    seen.add(k);
    const sugg = suggestions.find((s) => s.allocationId === a.id);
    rows.push({
      key: `al-${a.id}`,
      affaire: a.affaire,
      phase: a.phase,
      affaireId: a.affaireId,
      phaseId: a.phaseId,
      allocationId: a.id,
      billable: a.billable,
      existing: null,
      plannedHours: Number(a.hoursPerDay),
      isSuggestion: Boolean(sugg),
    });
  }

  const hasSuggestions = suggestions.some((s) => !s.isLocked) && !locked;
  const todayIso = isoDate(todayUtc());

  return (
    <div className="m-day">
      <div className="m-day__head" data-today={iso === todayIso} data-holiday={Boolean(holiday)}>
        <span>{fullDayLabel(date)}</span>
        {holiday ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: 'var(--warning)',
              fontSize: 12,
            }}
          >
            <PartyPopper size={13} /> {holiday.name}
          </span>
        ) : null}
        {locked ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: 'var(--text-muted)',
              fontSize: 11,
              marginLeft: 8,
            }}
          >
            <Lock size={12} /> Verrouillé
          </span>
        ) : null}
        <span className="m-day__total">{formatHHMM(total)}</span>
      </div>
      <div className="m-day__body">
        {timeOffs.map((t, i) => (
          <div
            className="m-block"
            key={`to-${i}`}
            style={{
              background: 'var(--glass)',
              // Demande de congé encore en attente de validation → bordure
              // pointillée (cohérent calendrier / Schedule).
              ...(t.status === 'PENDING'
                ? { border: '1px dashed var(--glass-border)', opacity: 0.8 }
                : {}),
            }}
          >
            <span
              className="m-block__dot"
              style={{ background: safeColor(t.timeOffType?.colorHex, 'var(--text-muted)') }}
            />
            <div style={{ minWidth: 0 }}>
              <div className="m-block__name">
                {t.timeOffType?.name || 'Congé'}
                {t.status === 'PENDING' ? ' • en attente' : ''}
              </div>
            </div>
            <span className="m-block__hours">
              {t.fullDay ? 'Jour' : formatHHMM(t.hoursPerDay)}
            </span>
          </div>
        ))}

        {rows.length === 0 && timeOffs.length === 0 ? (
          <div className="m-day__empty">Aucune affaire planifiée</div>
        ) : null}

        {rows.map((row) => (
          <div className="m-logrow" key={row.key} data-locked={locked}>
            <span
              className="m-block__dot"
              style={{ background: safeColor(row.affaire?.color) }}
            />
            <div className="m-logrow__main">
              <div className="m-block__name">{row.affaire?.name || '—'}</div>
              <div className="m-block__sub">
                {row.phase?.name ? `${row.phase.name} · ` : ''}
                {row.plannedHours != null ? `Planifié ${formatHHMM(row.plannedHours)}` : ''}
                {row.isSuggestion ? (
                  <span className="m-badge" style={{ marginLeft: 6, color: 'var(--warning)' }}>
                    Suggéré
                  </span>
                ) : null}
                {row.affaire?.planningOnly ? (
                  <span
                    className="m-badge"
                    title="Affaire en planification uniquement : imputations fermées"
                    style={{ marginLeft: 6, color: 'var(--warning)' }}
                  >
                    Planification
                  </span>
                ) : null}
              </div>
              {row.existing?.task?.name ? (
                <div className="m-block__sub m-logrow__meta">
                  <ListChecks size={12} aria-hidden="true" /> {row.existing.task.name}
                </div>
              ) : null}
              {row.existing?.notes ? (
                <div className="m-block__sub m-logrow__meta">
                  <StickyNote size={12} aria-hidden="true" /> {row.existing.notes}
                </div>
              ) : null}
            </div>
            <DurationInput
              value={row.existing ? Number(row.existing.hours) : null}
              // Affaire en planification uniquement : imputations fermées.
              readOnly={locked || Boolean(row.affaire?.planningOnly)}
              ariaLabel={`Heures ${row.affaire?.name || ''}`}
              placeholder={row.plannedHours != null ? formatHHMM(row.plannedHours) : '0:00'}
              // ≥ 16px : sous ce seuil, iOS zoome au focus et ne dézoome pas.
              style={{ fontSize: 16, width: 84, ...(savingKey === row.key ? { opacity: 0.5 } : {}) }}
              onChange={(hours) =>
                onSave(
                  row.key,
                  {
                    existing: row.existing,
                    affaireId: row.affaireId,
                    phaseId: row.phaseId,
                    allocationId: row.allocationId,
                    date: iso,
                    billable: row.billable,
                  },
                  hours,
                )
              }
            />
            {/* Saisie existante : détail (tâche, note) et suppression. */}
            {row.existing && onEdit ? (
              <span className="m-logrow__actions">
                <button
                  type="button"
                  className="m-iconbtn m-iconbtn--sm"
                  onClick={() => onEdit(row.existing, locked)}
                  title={locked ? 'Voir le détail' : 'Tâche, note, heures'}
                  aria-label={`Modifier l’imputation ${row.affaire?.name || ''}`}
                >
                  <Pencil size={15} />
                </button>
                {!locked ? (
                  <button
                    type="button"
                    className="m-iconbtn m-iconbtn--sm m-iconbtn--danger"
                    onClick={() => onEdit(row.existing, locked, true)}
                    title="Supprimer l’imputation"
                    aria-label={`Supprimer l’imputation ${row.affaire?.name || ''}`}
                  >
                    <Trash2 size={15} />
                  </button>
                ) : null}
              </span>
            ) : null}
          </div>
        ))}

        {hasSuggestions || (onAdd && !locked) ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
            {hasSuggestions ? (
              <button type="button" className="m-actionbtn" onClick={() => onLogAll(iso)}>
                <Zap size={15} /> Tout valider
              </button>
            ) : null}
            {/* Imputation hors planning (vue Tableau : « Ajouter une affaire »). */}
            {onAdd && !locked ? (
              <button type="button" className="m-actionbtn" onClick={() => onAdd(iso)}>
                <Plus size={15} /> Ajouter une affaire
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
